// @ts-strict-ignore
import Space from 'antd/lib/space';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import {
    AppPlatform,
    AutomationType,
    BuildStatus,
    CIBuildInfo,
    ReleaseStatus,
    ReleaseStepStatus,
    ReleaseStepType,
    RunwayResponseType,
    StabilityMonitoringService,
    WorkflowAndBranchData,
    WorkflowArgument,
} from '../../types';
import {
    antGrey,
    releaseStepDetailsContainerModuleStyle,
    releaseStepModuleTitleStyle,
    runwayColors,
    tagStyle,
    titleStyle,
} from '../styles/globalStyles';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import { DeleteOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { iconAndAltForIntegrationType } from '../../helpers/integrationHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { showArtifactsModuleEvenIfEmpty } from '../../helpers';
import { RootState } from '../../reducers/reducers';
import { buildStatusStringForBuild, statusColorForBuildStatus } from '../../helpers/displayHelpers';
import Link from 'antd/lib/typography/Link';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, convertTo24HourFormat, getLocaleDateTimeFormat } from '../../timeHelpers';
import Divider from 'antd/lib/divider';
import Tag from 'antd/lib/tag';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import {
    StartWorkflowRequest,
    TRIGGER_RELEASE_WORKFLOW_BUILD,
    triggerReleaseWorkflowBuild,
} from '../../actions/release';
import CommitInfoModule from './CommitInfoModule';
import ArtifactsModule from './ArtifactsModule';
import { WorkflowVariablesIndicator } from './WorkflowVariablesIndicator';
import { AppBuildComponent } from '../BuildComponents';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import {
    selectAppDistributionServices,
    selectAppIdentifiers,
    selectAppTimeFormatSetting,
    selectAutomationById,
    selectFirstAutomationOfType,
    selectRelease,
    selectStabilityMonitoringIntegration,
    selectStepRun,
} from '../../selectors/selectors';
import { globalMessage } from '../../utils/globalMessage';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';

interface WorkflowBuildModuleProps extends ReleaseStepComponentProps {
    releaseStepStatus: ReleaseStepStatus;
    platform: Nullable<AppPlatform>;
    workflowData?: WorkflowAndBranchData;
    sdkVersion?: string;
    buildInfo?: CIBuildInfo;
    moduleTitle?: string;
    autoUploadDsymsToStabilityMonitoringAutomationId: Nullable<string>;
    autoUploadBinaryArtifactToAppStoreAutomationId: Nullable<string>;
    autoDownloadArtifactsAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const distributionSteps = [ReleaseStepType.CIDistribution, ReleaseStepType.Submission, ReleaseStepType.VCSDistribution];

interface RunWorkflowModalFormValues {
    additionalWorkflowArguments: WorkflowArgument[];
}

function additionalWorkflowArgumentsToRecord(rows: WorkflowArgument[] | undefined): Record<string, string> {
    const out: Record<string, string> = {};
    for (const row of rows ?? []) {
        const k = (row?.argumentKey ?? '').trim();
        if (!k) {
            continue;
        }
        out[k] = row?.argumentValue ?? '';
    }
    return out;
}

/** Two equal key/value columns plus a fixed action column (delete or spacer) so all rows share the same input widths. */
const runWorkflowArgRowGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 40px',
    columnGap: 10,
    // Top-align so validation help under one field does not vertically center the sibling inputs.
    alignItems: 'start',
    marginBottom: 10,
};

const runWorkflowArgFormItemStyle: React.CSSProperties = { marginBottom: 0, width: '100%', minWidth: 0 };

const WorkflowBuildModule: React.FC<WorkflowBuildModuleProps> = (props: WorkflowBuildModuleProps) => {
    const dispatch = useDispatch();
    const isDistributionStep = distributionSteps.includes(props.releaseStepType);

    const artifactAutomations = useSelector((state: RootState) => {
        return {
            [AutomationType.AutoUploadDsymsToStabilityMonitoring]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadDsymsToStabilityMonitoringAutomationId,
            ),

            [AutomationType.AutoUploadBinaryArtifactToAppStore]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadBinaryArtifactToAppStoreAutomationId,
            ),
            [AutomationType.AutoDownloadArtifacts]: selectAutomationById(
                state,
                props.appId,
                props.autoDownloadArtifactsAutomationId,
            ),
        };
    });

    const ciBuild = props.buildInfo?.ciBuild;
    const associatedAppStoreBuild = props.buildInfo?.associatedAppStoreBuild;
    const workflowData = props.workflowData;

    const releaseStatus = useSelector((state: RootState) => selectRelease(state, props.releaseId).status);
    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, props.appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const triggerWorkflowIsLoadingReleaseStep = useIsLoading(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);
    const triggerWorkflowErrorReleaseStep = useError(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);
    const triggerWorkflowSuccessReleaseStep = useUpdated(TRIGGER_RELEASE_WORKFLOW_BUILD, props.releaseId);

    const stepRunId = props.stepRunComponentProps?.stepRunId;
    const stepRun = useSelector((state: RootState) => selectStepRun(state, stepRunId));

    const [
        triggerWorkflow,
        triggerWorkflowIsLoadingStepRun,
        triggerWorkflowResponseTypeStepRun,
        triggerWorkflowErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.TriggerBuildSystemWorkflow, props.stepRunComponentProps);

    const triggerWorkflowIsLoading = stepRun ? triggerWorkflowIsLoadingStepRun : triggerWorkflowIsLoadingReleaseStep;
    const triggerWorkflowError = stepRun ? triggerWorkflowErrorStepRun : triggerWorkflowErrorReleaseStep;
    const triggerWorkflowSuccess =
        stepRun ? triggerWorkflowResponseTypeStepRun === RunwayResponseType.Success : triggerWorkflowSuccessReleaseStep;

    const [icon, alt] = iconAndAltForIntegrationType(workflowData?.integrationType);

    const buildStatusText = buildStatusStringForBuild(ciBuild);
    const tagColor = statusColorForBuildStatus(ciBuild?.buildStatus);
    const commitLabel = ciBuild ? 'Commit' : 'Current head commit';
    const commit = ciBuild ? ciBuild.commit : (workflowData?.headCommit ?? '--');
    const commitMessage = ciBuild ? ciBuild.commitMessage : (workflowData?.headCommitMessage ?? '--');
    const commitAuthor = ciBuild ? ciBuild.commitAuthor : (workflowData?.headCommitAuthor ?? null);
    const finishedAtMoment = ciBuild?.buildFinishedAt ? dayjs(ciBuild.buildFinishedAt) : null;
    const artifacts = ciBuild ? ciBuild.artifacts : null;

    const [confirmRunWorkflowOpen, setConfirmRunWorkflowOpen] = React.useState(false);
    const [runWorkflowForm] = Form.useForm<RunWorkflowModalFormValues>();

    const fixedWorkflowVariableKeys = React.useMemo(
        () => new Set(Object.keys(workflowData?.workflowVariables ?? {})),
        [workflowData],
    );

    // Disable trigger button based on whether trigger automation is supported for this build system
    const triggerWorkflowAutomation = useSelector((state: RootState) =>
        selectFirstAutomationOfType(state, props.appId, AutomationType.AutoKickoffTriggerWorkflow),
    );

    const startWorkflowButtonDisabled =
        !workflowData ||
        triggerWorkflowAutomation?.unsupportedIntegrations.includes(workflowData.integrationType) === true;

    const titleTextStyle: React.CSSProperties = {
        ...titleStyle,
        color: antGrey.grey7,
        fontSize: 12,
    };

    const openPanelStateForStatus = (
        status: ReleaseStepStatus,
        isDistributionStep: boolean,
        buildStatus?: BuildStatus,
    ) => {
        switch (status) {
            case ReleaseStepStatus.Inactive:
                return ['workflow'];
            default:
                if (isDistributionStep && buildStatus === BuildStatus.Success) {
                    return [];
                }

                return ['workflow'];
        }
    };

    const openConfirmRunWorkflow = () => {
        setConfirmRunWorkflowOpen(true);
    };

    React.useEffect(() => {
        if (confirmRunWorkflowOpen) {
            runWorkflowForm.resetFields();
            runWorkflowForm.setFieldsValue({ additionalWorkflowArguments: [] });
        }
    }, [confirmRunWorkflowOpen, runWorkflowForm]);

    const executeRunWorkflow = (additionalWorkflowVariables: Record<string, string>) => {
        if (!workflowData) {
            return;
        }
        const { workflowType } = workflowData;
        const hasAdditional = Object.keys(additionalWorkflowVariables).length > 0;

        if (stepRun) {
            triggerWorkflow({
                workflowType,
                ...(hasAdditional ? { additionalWorkflowVariables } : {}),
            });
            return;
        }

        const request: StartWorkflowRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
            workflowType,
            ...(hasAdditional ? { additionalWorkflowVariables } : {}),
        };

        dispatch(triggerReleaseWorkflowBuild(request));
    };

    React.useEffect(() => {
        if (triggerWorkflowError) {
            globalMessage.error(triggerWorkflowError);
        }
    }, [triggerWorkflowError]);

    React.useEffect(() => {
        if (triggerWorkflowSuccess) {
            globalMessage.success(
                'Your workflow has been triggered! It may take a few seconds for the updated workflow status to appear in Runway.',
            );
            setConfirmRunWorkflowOpen(false);
        }
    }, [triggerWorkflowSuccess]);

    const justifyContent = props.moduleTitle ? 'space-between' : 'flex-end';

    const stabilityMonitoring = useSelector((state: RootState) =>
        selectStabilityMonitoringIntegration(state, props.appId),
    ) as StabilityMonitoringService;
    const appIdentifiers = useSelector((state: RootState) => selectAppIdentifiers(state, props.appId));
    const version = useSelector((state: RootState) => selectRelease(state, props.releaseId)).version;

    const showArtifactsModule =
        (!!artifacts && artifacts.length > 0) || showArtifactsModuleEvenIfEmpty(artifactAutomations);

    const supportsTriggerWithWorkflowVariables = workflowData?.supportsTriggerWithWorkflowVariables === true;

    return (
        <div
            style={{
                marginBottom: 24,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minWidth: 0,
            }}
        >
            <Modal
                title="Run workflow"
                open={confirmRunWorkflowOpen}
                okText="Run workflow"
                destroyOnClose
                onOk={async () => {
                    if (!supportsTriggerWithWorkflowVariables) {
                        executeRunWorkflow({});
                        return;
                    }
                    try {
                        const values = await runWorkflowForm.validateFields();
                        const additionalRecord = additionalWorkflowArgumentsToRecord(
                            values.additionalWorkflowArguments,
                        );
                        executeRunWorkflow(additionalRecord);
                    } catch (e: unknown) {
                        const err = e as { errorFields?: unknown[]; message?: string };
                        if (err?.errorFields?.length) {
                            return;
                        }
                        globalMessage.error(err?.message ?? 'Could not validate the form.');
                    }
                }}
                onCancel={() => setConfirmRunWorkflowOpen(false)}
                confirmLoading={triggerWorkflowIsLoading}
                okButtonProps={{ disabled: triggerWorkflowIsLoading }}
                width={560}
            >
                {workflowData && (
                    <>
                        <Text style={{ color: antGrey.grey7, display: 'block', marginBottom: 20 }}>
                            Runway will trigger the{' '}
                            <Text style={{ color: runwayColors.black }}>{workflowData.workflowName || '—'}</Text>{' '}
                            workflow on branch <Text code>{workflowData.branch || '—'}</Text>
                            {supportsTriggerWithWorkflowVariables ?
                                <> with any workflow arguments below.</>
                            :   '.'}
                        </Text>
                        {supportsTriggerWithWorkflowVariables && (
                            <Form
                                form={runWorkflowForm}
                                layout="vertical"
                                style={{ marginTop: 4 }}
                                initialValues={{ additionalWorkflowArguments: [] as WorkflowArgument[] }}
                            >
                                {Object.entries(workflowData.workflowVariables ?? {}).length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        {Object.entries(workflowData.workflowVariables ?? {}).map(([k, v]) => (
                                            <div key={k} style={runWorkflowArgRowGridStyle}>
                                                <Input disabled value={k} style={{ width: '100%' }} />
                                                <Input disabled value={v ?? ''} style={{ width: '100%' }} />
                                                <span aria-hidden="true" />
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <Form.List name="additionalWorkflowArguments">
                                    {(fields, { add, remove }) => (
                                        <div>
                                            {fields.map((field) => (
                                                <div key={field.key} style={runWorkflowArgRowGridStyle}>
                                                    <Form.Item
                                                        name={[field.name, 'argumentKey']}
                                                        rules={[
                                                            { required: true, message: 'Enter a key' },
                                                            ({ getFieldValue }) => ({
                                                                validator: async (_, value) => {
                                                                    const trimmed = (value ?? '').trim();
                                                                    if (!trimmed) {
                                                                        return;
                                                                    }
                                                                    if (fixedWorkflowVariableKeys.has(trimmed)) {
                                                                        throw new Error(
                                                                            'This key is already set in integration settings',
                                                                        );
                                                                    }
                                                                    const rows: WorkflowArgument[] =
                                                                        getFieldValue('additionalWorkflowArguments') ||
                                                                        [];
                                                                    const sameKey = rows.filter(
                                                                        (r) =>
                                                                            (r?.argumentKey ?? '').trim() === trimmed,
                                                                    );
                                                                    if (sameKey.length > 1) {
                                                                        throw new Error('Duplicate key');
                                                                    }
                                                                },
                                                            }),
                                                        ]}
                                                        style={runWorkflowArgFormItemStyle}
                                                    >
                                                        <Input placeholder="Key" />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={[field.name, 'argumentValue']}
                                                        rules={[{ required: true, message: 'Enter a value' }]}
                                                        style={runWorkflowArgFormItemStyle}
                                                    >
                                                        <Input placeholder="Value" />
                                                    </Form.Item>
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'flex-start',
                                                            paddingTop: 4,
                                                        }}
                                                    >
                                                        <Button
                                                            type="link"
                                                            danger
                                                            icon={
                                                                <DeleteOutlined
                                                                    style={{ color: runwayColors.statusRed }}
                                                                />
                                                            }
                                                            onClick={() => remove(field.name)}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            <Button
                                                size="small"
                                                icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                                                onClick={() => add({ argumentKey: '', argumentValue: '' })}
                                            >
                                                {(
                                                    fields.length === 0 &&
                                                    Object.entries(workflowData.workflowVariables ?? {}).length == 0
                                                ) ?
                                                    'Add workflow argument'
                                                :   'Add another workflow argument'}
                                            </Button>
                                        </div>
                                    )}
                                </Form.List>
                            </Form>
                        )}
                    </>
                )}
            </Modal>
            <div style={{ display: 'flex', justifyContent, alignItems: 'baseline' }}>
                {props.moduleTitle && <Text style={{ fontSize: 20, color: antGrey.grey7 }}>{props.moduleTitle}</Text>}
                <Space size={16} style={{ marginBottom: 12 }}>
                    <Space
                        split={<Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />}
                        align="center"
                    >
                        <Text style={{ ...releaseStepModuleTitleStyle }}>Workflow</Text>
                        <Space>
                            {workflowData &&
                                (workflowData.workflowUrl ?
                                    <Link href={workflowData.workflowUrl} target="_blank" strong>
                                        {workflowData.workflowName}
                                    </Link>
                                :   <Text style={{ color: antGrey.grey8 }}>{workflowData.workflowName}</Text>)}
                            <WorkflowVariablesIndicator workflowVariables={workflowData?.workflowVariables} />
                        </Space>
                    </Space>
                    <PermissionedActionButton
                        releaseId={props.releaseId}
                        actionType={RunwayUserAction.UATriggerCIWorkflow}
                        icon={<PlayCircleOutlined style={{ color: runwayColors.info }} />}
                        onClick={openConfirmRunWorkflow}
                        disabled={startWorkflowButtonDisabled || releaseStatus === ReleaseStatus.Completed}
                        loading={triggerWorkflowIsLoading}
                    >
                        Run workflow
                    </PermissionedActionButton>
                </Space>
            </div>
            <div
                style={{
                    ...releaseStepDetailsContainerModuleStyle,
                }}
            >
                <Collapse
                    defaultActiveKey={openPanelStateForStatus(
                        props.releaseStepStatus,
                        isDistributionStep,
                        ciBuild?.buildStatus,
                    )}
                    className="adjust-collapse-icon"
                    ghost
                >
                    <Collapse.Panel
                        className="no-padding-collapse-panel"
                        key={'workflow'}
                        header={
                            <Space align="center">
                                {icon && <img src={icon} alt={alt} />}
                                <Tag
                                    color={tagColor}
                                    style={{
                                        ...tagStyle,
                                        fontSize: props.releaseStepType == ReleaseStepType.RollbackBuild ? 11 : 14,
                                        fontWeight: props.releaseStepType == ReleaseStepType.RollbackBuild ? 600 : 400,
                                        lineHeight:
                                            props.releaseStepType == ReleaseStepType.RollbackBuild ? '22px' : null,
                                    }}
                                >
                                    {buildStatusText}
                                </Tag>
                                {!ciBuild && <Text type="secondary">No workflow runs</Text>}
                                <Space split={<Divider type="vertical"></Divider>} align="center">
                                    <Space>
                                        {ciBuild?.buildIdentifier &&
                                            (ciBuild.buildUrl ?
                                                <Link href={ciBuild.buildUrl} target="_blank" strong>
                                                    Build #{ciBuild.buildIdentifier}
                                                </Link>
                                            :   <Text style={{ color: antGrey.grey8 }} strong>
                                                    Build #{ciBuild.buildIdentifier}
                                                </Text>)}
                                        {finishedAtMoment && (
                                            <Text type="secondary">
                                                - finished{' '}
                                                {finishedAtMoment.format(
                                                    getLocaleDateTimeFormat('short', use24HourTimeFormat),
                                                )}
                                            </Text>
                                        )}
                                    </Space>
                                    {associatedAppStoreBuild && (
                                        <AppBuildComponent
                                            integrationId={associatedAppStoreBuild.integrationId}
                                            buildIdentifier={associatedAppStoreBuild.buildNumber}
                                            isProcessed={associatedAppStoreBuild.isProcessed}
                                        />
                                    )}
                                </Space>
                            </Space>
                        }
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                            <CommitInfoModule
                                commitLabel={commitLabel}
                                commit={commit}
                                commitAuthor={commitAuthor}
                                commitMessage={commitMessage}
                                branch={workflowData?.branch ?? '--'}
                            />
                            {props.platform === AppPlatform.ReactNativeOTA &&
                                ciBuild?.buildStatus === BuildStatus.Success && (
                                    <div
                                        style={{
                                            ...releaseStepDetailsContainerModuleStyle,
                                            backgroundColor: antGrey.grey2,
                                        }}
                                    >
                                        <Text style={titleTextStyle}>Details</Text>
                                        <List
                                            split={false}
                                            style={{ marginTop: 12, paddingLeft: 12, paddingRight: 12 }}
                                        >
                                            {finishedAtMoment && (
                                                <List.Item style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Text style={{ color: antGrey.grey8 }}>
                                                        • Uploaded at{' '}
                                                        {finishedAtMoment.format(
                                                            use24HourTimeFormat ?
                                                                convertTo24HourFormat(DatetimeFormat.TimeShortDate)
                                                            :   DatetimeFormat.TimeShortDate,
                                                        )}
                                                    </Text>
                                                </List.Item>
                                            )}
                                            {props.sdkVersion && (
                                                <List.Item style={{ paddingTop: 0, paddingBottom: 0 }}>
                                                    <Text style={{ color: antGrey.grey8 }}>
                                                        • SDK version: {props.sdkVersion}
                                                    </Text>
                                                </List.Item>
                                            )}
                                        </List>
                                    </div>
                                )}
                            {!!ciBuild && showArtifactsModule && (
                                <ArtifactsModule
                                    appId={props.appId}
                                    orgId={props.orgId}
                                    releaseId={props.releaseId}
                                    version={version}
                                    artifacts={artifacts ?? []}
                                    services={distributionServices}
                                    stabilityMonitoring={stabilityMonitoring}
                                    appIdentifiers={appIdentifiers}
                                    buildStatus={ciBuild.buildStatus}
                                    canRetry={true}
                                    showHeader={true}
                                    isPublic={false}
                                    stepRunComponentProps={props.stepRunComponentProps}
                                />
                            )}
                        </div>
                    </Collapse.Panel>
                </Collapse>
            </div>
        </div>
    );
};

export default WorkflowBuildModule;
